/* eslint-disable import/order */
import Slug from './components/Breadcrumbs/Slug';
import LazyFixedFooterBanner from './components/LazyFixedFooterBanner';
import { CATALOG_AMPLITUDE_PAGE_NAME } from './constants';
import Catalog from './containers/App';
import AppContext from './context/AppContext';
import type { AppProps } from './types';
import { isCurrentPageCoursesFree } from './utils';
import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import {
  NOTIFICATIONS_FETCH,
  useGetNotification,
} from '@crehana/compass.notification';
import { useIntersectionObserver } from '@crehana/react-hooks';
import { MainFooter, MainMenu } from '@crehana/web';
import CallCenterDialog from '@crehana/web/dist/CallCenterDialog';
import { useMe } from '@crehana/web/dist/ReactContexts/AuthContext';
import { ContactSourceEnum as TrackingSource } from '@crehana/web/dist/__generated__/graphql-schema';
import useFeatureFlagQuery from 'Jsx/Shared/Hooks/useFeatureFlagQuery/useFeatureFlagQuery';
import IdleComponent from 'Jsx/Shared/IdleComponent';
import Media from 'Jsx/Shared/ui/Media';
import type { InitialStateResult } from 'Jsx/Utils/getBaseInitialState';
import React, { FC, Suspense, lazy, useEffect, useState } from 'react';

// This component is only needed for logged in users
const LazyNotificationDiscount = lazy(
  () =>
    import(
      /* webpackChunkName: "catalog-user-discount" */
      'Jsx/Catalog/components/UserDiscount'
    ),
);

const LazyCoursePreview = lazy(
  () =>
    import(
      /* webpackChunkName: "catalog-notification-discount" */
      'Jsx/Shared/CoursePreview'
    ),
);

export type ExtraState = { pathname: string };

const App: FC<InitialStateResult<AppProps & ExtraState>> = props => {
  const { logEvent } = useAmplitude();
  const {
    countryPrefix,
    pathname,
    banners,
    initialSeoLongDescription,
    featureFlagStartMarathonPopup,
  } = props;

  const { me, loading: loadingMeQuery } = useMe();
  const [category, setCategory] = React.useState('');
  const [subCategory, setSubCategory] = React.useState('');
  const [isIntersectingFooter, setIsIntersectingFooter] = useState(false);
  const footerRef = React.useRef(null);
  const observer = useIntersectionObserver(footerRef, {
    freezeOnceVisible: false,
  });

  const [currentPageIsCoursesFree, setCurrentPageIsCoursesFree] = useState(
    isCurrentPageCoursesFree(pathname),
  );

  const isOrganization = Boolean(!loadingMeQuery && me?.organization);
  const isPremiumMember = Boolean(!loadingMeQuery && me?.isPremiumMember);

  /**
   * The SlideHeader (or BannerSlider) and the SignupBanner can't be rendered together.
   * Only one of them can render and it'll depends of the value of the location.pathname === '/cursos-gratis/'
   */
  const shouldRenderCourseFreeBanner =
    currentPageIsCoursesFree && !loadingMeQuery && !me;
  const shouldRenderSliderHeader =
    !isOrganization &&
    !shouldRenderCourseFreeBanner &&
    !loadingMeQuery &&
    banners.length;
  const shouldRenderPremiumBar = !isPremiumMember && !isOrganization;

  const trackPageRender = (_category: string, _subCategory: string): void => {
    setCategory(_category);
    setSubCategory(_subCategory);
  };

  const { isActive: showNotification } = useFeatureFlagQuery({
    flagKey: 'user_notification_web',
  });

  const {
    notificationData,
    error,
    hasMore,
    loading: loadingNotification,
    onFetchMore,
    totalCount,
  } = useGetNotification({
    first: NOTIFICATIONS_FETCH,
    isSkip: !showNotification,
  });

  useEffect(() => {
    logEvent({
      event: 'Render Catalog',
      data: {
        ...getSourceEventParams(),
        'Course Category': category,
        'Course Subcategory': subCategory,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isIntersecting = observer?.isIntersecting ?? false;

    if (isIntersecting !== isIntersectingFooter) {
      setIsIntersectingFooter(isIntersecting);
    }
  }, [isIntersectingFooter, observer?.isIntersecting]);

  const onChangeRoute = () => {
    setCurrentPageIsCoursesFree(isCurrentPageCoursesFree(pathname));
  };

  return (
    <>
      <MainMenu
        countryPrefix={countryPrefix}
        amplitudePageName={CATALOG_AMPLITUDE_PAGE_NAME}
        customUrls={{
          register: `/onboarding/?nextUrl=${window?.location.href}`,
        }}
        layoutConfig={{
          hideNotifications: !showNotification,
        }}
        notification={{
          notificationData,
          error,
          hasMore,
          loading: loadingNotification,
          onFetchMore,
          totalCount,
          totalUnRead: notificationData.totalUnRead,
        }}
      />

      <AppContext value={props}>
        <Catalog
          shouldRenderCourseFreeBanner={shouldRenderCourseFreeBanner}
          trackPageRender={trackPageRender}
          initialSeoLongDescription={initialSeoLongDescription}
          featureFlagStartMarathonPopup={Boolean(featureFlagStartMarathonPopup)}
          shouldRenderSliderHeader={!!shouldRenderSliderHeader}
          forceCloseFloatingFilter={isIntersectingFooter}
        />

        <Slug onChangeRoute={onChangeRoute} />
      </AppContext>

      <div ref={footerRef}>
        <MainFooter />
      </div>
      <IdleComponent>
        {!loadingMeQuery && !!me ? (
          <Suspense fallback={null}>
            <LazyNotificationDiscount />
          </Suspense>
        ) : null}

        {shouldRenderPremiumBar && (
          <Media from="md">
            <LazyFixedFooterBanner />
          </Media>
        )}

        {countryPrefix !== 'br' &&
          !loadingMeQuery &&
          !me?.hasOrganization &&
          !me?.isPremiumMember && (
            <CallCenterDialog
              identifier="catalog"
              secondsToTrigger={3}
              trackingSource={TrackingSource.SOURCE_CALL_CENTER_CATALOG_POPUP}
              configuration="only-timer"
              dialogSource="Catalog"
            />
          )}
        <Media from="md">
          <Suspense fallback={null}>
            <LazyCoursePreview
              source={CATALOG_AMPLITUDE_PAGE_NAME}
              isLogged={!!me}
            />
          </Suspense>
        </Media>
      </IdleComponent>
    </>
  );
};

export default App;
